'use client'

import { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import { ReviewCardData } from '@/lib/google-reviews/types'
import { StarRating } from './StarRating'
import { GoogleLogo } from './GoogleLogo'
import { FacebookLogo } from './FacebookLogo'

interface GoogleReviewCardProps {
  review: ReviewCardData
  /** Fill the container and grow with the text, for a reading page rather than the marquee. */
  fluid?: boolean
}

function renderTextWithHighlight(text: string, highlightedText?: string) {
  if (!highlightedText) {
    return text
  }

  const index = text.indexOf(highlightedText)
  if (index === -1) {
    return text
  }

  const before = text.slice(0, index)
  const after = text.slice(index + highlightedText.length)

  return (
    <>
      {before}
      <mark className="px-0.5" style={{ backgroundColor: '#ffcd3640' }}>{highlightedText}</mark>
      {after}
    </>
  )
}

export function GoogleReviewCard({ review, fluid = false }: GoogleReviewCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const isFacebook = review.source === 'facebook'

  // Senja-style: Use ResizeObserver to detect if text overflows (scrollHeight > clientHeight)
  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const checkOverflow = () => {
      // Only check when collapsed - if scrollHeight > clientHeight, text is truncated
      if (!expanded) {
        setShowReadMore(el.scrollHeight > el.clientHeight)
      }
    }

    // Check initially
    checkOverflow()

    // Watch for resize
    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(el)

    return () => resizeObserver.disconnect()
  }, [expanded, review.text])

  return (
    <article
      className={`flex flex-col rounded-lg border border-gray-200 bg-white p-4 ${
        fluid ? 'w-full' : 'w-[300px] flex-shrink-0'
      }`}
      style={{
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.07)',
        minHeight: fluid ? undefined : '390px',
        height: fluid || expanded ? 'auto' : '390px',
        transition: 'height 0.5s',
      }}
    >
      {/* Header: Avatar + Name + Source Logo */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {review.authorAvatar ? (
            <Image
              src={review.authorAvatar}
              alt={`${review.authorName} avatar`}
              width={42}
              height={42}
              className="rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-green-100 text-lg font-semibold text-green-700">
              {review.authorName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-gray-900">
            {review.authorName}
          </p>
          {review.authorSubtitle && (
            <p className="truncate text-xs text-gray-500">
              {review.authorSubtitle}
            </p>
          )}
        </div>
        <a
          href={review.sourceUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex-shrink-0"
          aria-label={isFacebook ? 'View on Facebook' : 'View on Google'}
        >
          {isFacebook ? (
            <FacebookLogo className="h-5 w-5" />
          ) : (
            <GoogleLogo className="h-5 w-5" />
          )}
        </a>
      </div>

      {/* Star Rating */}
      <StarRating rating={review.rating} className="my-2" />

      {/* Review Text - Senja structure: card grows when expanded */}
      <div className="min-h-0 flex-1 text-left">
        <div>
          <div
            ref={textRef}
            className="text-sm leading-relaxed text-gray-700"
            style={expanded ? {} : {
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 10,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {renderTextWithHighlight(review.text, review.highlightedText)}
          </div>
          {/* Read more / Read less */}
          {(showReadMore || expanded) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="border-none text-sm text-gray-600"
              style={{
                opacity: 0.8,
                transform: 'scale(0.95)',
                marginTop: '1px',
                transformOrigin: 'left top',
              }}
            >
              <span>{expanded ? 'Read less' : 'Read more'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Date Footer */}
      <div className="mt-auto pt-2">
        <p className="text-xs text-gray-500 opacity-70">{review.date}</p>
      </div>
    </article>
  )
}
